/**
 * LeadForm - Create and edit lead form (full page)
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Users,
  Mail,
  Phone,
  Calendar,
  School,
  Tag,
  MapPin,
  Loader2,
  Save,
  X,
  CheckCircle,
} from 'lucide-react';
import { useLead, useCreateLead, useUpdateLead } from '../../hooks/useCRM';
import { useUsers } from '../../hooks/useUsers';

// Valid lead sources
const LEAD_SOURCES = [
  { value: 'website', label: 'Website' },
  { value: 'referral', label: 'Referral' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'phone_inquiry', label: 'Phone Inquiry' },
  { value: 'email_inquiry', label: 'Email Inquiry' },
  { value: 'ad_campaign', label: 'Ad Campaign' },
  { value: 'event', label: 'Event' },
  { value: 'import', label: 'Import' },
  { value: 'other', label: 'Other' },
];

// Pipeline stages
const PIPELINE_STAGES = [
  { value: 'new', label: 'New Lead', color: 'bg-gray-100 text-gray-700' },
  { value: 'contacted', label: 'Contacted', color: 'bg-blue-100 text-blue-700' },
  { value: 'tour_scheduled', label: 'Tour Scheduled', color: 'bg-purple-100 text-purple-700' },
  { value: 'tour_completed', label: 'Tour Completed', color: 'bg-green-100 text-green-700' },
  { value: 'enrollment_pending', label: 'Enrollment Pending', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'enrolled', label: 'Enrolled', color: 'bg-green-100 text-green-800' },
  { value: 'lost', label: 'Lost', color: 'bg-red-100 text-red-700' },
];

// Program types
const PROGRAM_TYPES = [
  'Preschool',
  'Pre-Kindergarten',
  'Kindergarten',
  'Elementary',
  'After School',
  'Summer Camp',
  'Tutoring',
  'Special Needs',
];

const LeadForm = () => {
  const { tenantId, leadId } = useParams();
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const _location = useLocation();
  
  // Determine mode based on URL
  const isEditMode = leadId !== 'new' && leadId !== undefined;
  
  // Fetch existing lead if in edit mode
  const { data: existingLead, isLoading: isLoadingLead } = useLead(
    isEditMode ? leadId : null
  );
  
  // Fetch staff for assignment
  const { data: usersData } = useUsers();
  const staffMembers = usersData?.users?.filter(
    (u) => u.role === 'admin' || u.role === 'employee' || u.role === 'super_admin'
  ) || [];
  
  // Mutations
  const createLeadMutation = useCreateLead();
  const updateLeadMutation = useUpdateLead();
  
  // Form state
  const [formData, setFormData] = useState({
    // Parent Info
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    
    // Student Info
    childName: '',
    childBirthDate: '',
    childAge: '',
    childCurrentSchool: '',
    childGrade: '',
    
    // Interest
    interestedProgramTypes: [],
    desiredStartDate: '',
    notes: '',
    
    // Source & Assignment
    source: '',
    sourceDetails: '',
    assignedTo: '',
    
    // Status
    currentStage: 'new',
    status: 'active',
    estimatedValue: '',
    
    // Tags
    tags: [],
  });
  
  const [errors, setErrors] = useState({});
  const [newTag, setNewTag] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Load existing lead data in edit mode
  useEffect(() => {
    if (isEditMode && existingLead) {
      setFormData({
        firstName: existingLead.firstName || '',
        lastName: existingLead.lastName || '',
        email: existingLead.email || '',
        phone: existingLead.phone || '',
        address: existingLead.address || '',
        childName: existingLead.childInfo?.name || '',
        childBirthDate: existingLead.childInfo?.dateOfBirth || '',
        childAge: existingLead.childInfo?.age || '',
        childCurrentSchool: existingLead.childInfo?.currentSchool || '',
        childGrade: existingLead.childInfo?.grade || '',
        interestedProgramTypes: existingLead.interests?.programTypes || [],
        desiredStartDate: existingLead.interests?.startDate || '',
        notes: existingLead.notes || '',
        source: existingLead.source || '',
        sourceDetails: existingLead.sourceDetails || '',
        assignedTo: existingLead.assignedTo?.id || '',
        currentStage: existingLead.currentStage || 'new',
        status: existingLead.status || 'active',
        estimatedValue: existingLead.estimatedValue || '',
        tags: existingLead.tags || [],
      });
    }
  }, [isEditMode, existingLead]);
  
  // Calculate age from birth date
  const calculateAge = (birthDate) => {
    if (!birthDate) return '';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };
  
  // Handle birth date change
  const handleBirthDateChange = (e) => {
    const birthDate = e.target.value;
    const age = calculateAge(birthDate);
    setFormData((prev) => ({
      ...prev,
      childBirthDate: birthDate,
      childAge: age.toString(),
    }));
  };
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'interestedProgramTypes') {
        const programValue = e.target.dataset.value;
        setFormData((prev) => ({
          ...prev,
          interestedProgramTypes: checked
            ? [...prev.interestedProgramTypes, programValue]
            : prev.interestedProgramTypes.filter((p) => p !== programValue),
        }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };
  
  // Handle tag management
  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }));
      setNewTag('');
    }
  };
  
  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tagToRemove),
    }));
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.source) {
      newErrors.source = 'Lead source is required';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const submitData = {
        ...formData,
        estimatedValue: formData.estimatedValue
          ? parseFloat(formData.estimatedValue)
          : null,
        childAge: formData.childAge ? parseInt(formData.childAge, 10) : null,
      };
      
      if (isEditMode) {
        await updateLeadMutation.mutateAsync({
          id: leadId,
          data: submitData,
        });
      } else {
        await createLeadMutation.mutateAsync(submitData);
      }
      
      setShowSuccess(true);
      setTimeout(() => {
        navigate(`/${tenantId}/dashboard/crm/leads`);
      }, 1500);
    } catch (error) {
      console.error('Failed to save lead:', error);
      setErrors({
        submit: error.message || 'Failed to save lead. Please try again.',
      });
    }
  };
  
  // Handle cancel
  const handleCancel = () => {
    navigate(-1);
  };
  
  if (isEditMode && isLoadingLead) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-purple-600" size={32} />
      </div>
    );
  }
  
  return (
    <div className="animate-fade-in max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleCancel}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditMode ? 'Edit Lead' : 'Add New Lead'}
            </h1>
            <p className="text-gray-500">
              {isEditMode
                ? 'Update lead information and status'
                : 'Capture new prospect information'}
            </p>
          </div>
        </div>
        
        {showSuccess && (
          <div className="flex items-center space-x-2 text-green-600 bg-green-50 px-4 py-2 rounded-lg">
            <CheckCircle size={20} />
            <span className="font-medium">Saved successfully!</span>
          </div>
        )}
      </div>
      
      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Error Banner */}
        {errors.submit && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {errors.submit}
          </div>
        )}
        
        {/* Parent Information */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-2 mb-4">
            <User className="text-purple-600" size={20} />
            <h2 className="text-lg font-semibold text-gray-900">Parent Information</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={`form-input ${errors.firstName ? 'border-red-500' : ''}`}
                placeholder="Parent's first name"
              />
              {errors.firstName && (
                <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>
              )}
            </div>
            
            <div>
              <label className="form-label">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className={`form-input ${errors.lastName ? 'border-red-500' : ''}`}
                placeholder="Parent's last name"
              />
              {errors.lastName && (
                <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>
              )}
            </div>
            
            <div>
              <label className="form-label">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`form-input pl-10 ${errors.email ? 'border-red-500' : ''}`}
                  placeholder="parent@example.com"
                />
              </div>
              {errors.email && (
                <p className="text-xs text-red-500 mt-1">{errors.email}</p>
              )}
            </div>
            
            <div>
              <label className="form-label">Phone</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input pl-10"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            
            <div className="md:col-span-2">
              <label className="form-label">Address</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="form-input pl-10"
                  placeholder="Full address"
                />
              </div>
            </div>
          </div>
        </section>
        
        {/* Student Information */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Users className="text-purple-600" size={20} />
            <h2 className="text-lg font-semibold text-gray-900">Student Information</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Child's Name</label>
              <input
                type="text"
                name="childName"
                value={formData.childName}
                onChange={handleChange}
                className="form-input"
                placeholder="Child's full name"
              />
            </div>
            
            <div>
              <label className="form-label">Date of Birth</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="date"
                  name="childBirthDate"
                  value={formData.childBirthDate}
                  onChange={handleBirthDateChange}
                  className="form-input pl-10"
                />
              </div>
            </div>
            
            <div>
              <label className="form-label">Age</label>
              <input
                type="number"
                name="childAge"
                value={formData.childAge}
                onChange={handleChange}
                className="form-input"
                placeholder="Age in years"
                min="0"
                max="18"
              />
            </div>
            
            <div>
              <label className="form-label">Current School</label>
              <div className="relative">
                <School className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  name="childCurrentSchool"
                  value={formData.childCurrentSchool}
                  onChange={handleChange}
                  className="form-input pl-10"
                  placeholder="Current school (if any)"
                />
              </div>
            </div>
            
            <div>
              <label className="form-label">Grade Level</label>
              <select
                name="childGrade"
                value={formData.childGrade}
                onChange={handleChange}
                className="form-input"
              >
                <option value="">Select grade...</option>
                <option value="Pre-K">Pre-K</option>
                <option value="Kindergarten">Kindergarten</option>
                <option value="1st Grade">1st Grade</option>
                <option value="2nd Grade">2nd Grade</option>
                <option value="3rd Grade">3rd Grade</option>
                <option value="4th Grade">4th Grade</option>
                <option value="5th Grade">5th Grade</option>
                <option value="6th Grade">6th Grade</option>
                <option value="7th Grade">7th Grade</option>
                <option value="8th Grade">8th Grade</option>
                <option value="High School">High School</option>
              </select>
            </div>
          </div>
        </section>
        
        {/* Program Interest */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-2 mb-4">
            <School className="text-purple-600" size={20} />
            <h2 className="text-lg font-semibold text-gray-900">Program Interest</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="form-label">Programs of Interest</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                {PROGRAM_TYPES.map((program) => (
                  <label
                    key={program}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      name="interestedProgramTypes"
                      data-value={program}
                      checked={formData.interestedProgramTypes.includes(program)}
                      onChange={handleChange}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">{program}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div>
              <label className="form-label">Desired Start Date</label>
              <div className="relative max-w-xs">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="date"
                  name="desiredStartDate"
                  value={formData.desiredStartDate}
                  onChange={handleChange}
                  className="form-input pl-10"
                />
              </div>
            </div>
            
            <div>
              <label className="form-label">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="form-input"
                rows={4}
                placeholder="Additional notes about this lead..."
              />
            </div>
          </div>
        </section>
        
        {/* Source & Assignment */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Tag className="text-purple-600" size={20} />
            <h2 className="text-lg font-semibold text-gray-900">Source & Assignment</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">
                Lead Source <span className="text-red-500">*</span>
              </label>
              <select
                name="source"
                value={formData.source}
                onChange={handleChange}
                className={`form-input ${errors.source ? 'border-red-500' : ''}`}
              >
                <option value="">Select source...</option>
                {LEAD_SOURCES.map((source) => (
                  <option key={source.value} value={source.value}>
                    {source.label}
                  </option>
                ))}
              </select>
              {errors.source && (
                <p className="text-xs text-red-500 mt-1">{errors.source}</p>
              )}
            </div>
            
            <div>
              <label className="form-label">Source Details</label>
              <input
                type="text"
                name="sourceDetails"
                value={formData.sourceDetails}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g., Facebook ad, Referrer name, etc."
              />
            </div>
            
            <div>
              <label className="form-label">Assigned To</label>
              <select
                name="assignedTo"
                value={formData.assignedTo}
                onChange={handleChange}
                className="form-input"
              >
                <option value="">-- Unassigned --</option>
                {staffMembers.map((staff) => (
                  <option key={staff.id} value={staff.id}>
                    {staff.firstName} {staff.lastName}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="form-label">Estimated Value ($)</label>
              <input
                type="number"
                name="estimatedValue"
                value={formData.estimatedValue}
                onChange={handleChange}
                className="form-input"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
          </div>
          
          {/* Tags */}
          <div className="mt-4">
            <label className="form-label">Tags</label>
            <div className="flex items-center space-x-2 mt-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                className="form-input max-w-xs"
                placeholder="Add a tag..."
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-purple-100 text-purple-700"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-1 hover:text-purple-900"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </section>
        
        {/* Status */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-2 mb-4">
            <CheckCircle className="text-purple-600" size={20} />
            <h2 className="text-lg font-semibold text-gray-900">Pipeline Status</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Current Stage</label>
              <select
                name="currentStage"
                value={formData.currentStage}
                onChange={handleChange}
                className="form-input"
              >
                {PIPELINE_STAGES.map((stage) => (
                  <option key={stage.value} value={stage.value}>
                    {stage.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="form-label">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="form-input"
              >
                <option value="active">Active</option>
                <option value="archived">Archived</option>
                <option value="converted">Converted</option>
              </select>
            </div>
          </div>
          
          {/* Stage Badge Preview */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500 mb-2">Current Stage Preview:</p>
            {(() => {
              const stage = PIPELINE_STAGES.find((s) => s.value === formData.currentStage);
              return stage ? (
                <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${stage.color}`}>
                  {stage.label}
                </span>
              ) : null;
            })()}
          </div>
        </section>
        
        {/* Actions */}
        <div className="flex items-center justify-end space-x-4 pt-4">
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            disabled={createLeadMutation.isPending || updateLeadMutation.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary flex items-center space-x-2"
            disabled={createLeadMutation.isPending || updateLeadMutation.isPending}
          >
            {createLeadMutation.isPending || updateLeadMutation.isPending ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save size={18} />
                <span>{isEditMode ? 'Update Lead' : 'Create Lead'}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LeadForm;
